/**
 * The Windows-services vocabulary: a hand port of `ServiceLimits.StateLabel` / `StartupTypeLabel`
 * in `Contracts/Services.cs`, plus the list filters, which live here so they can be tested without
 * a canvas.
 */
import {
  ServiceFailureAction,
  ServiceStartupType,
  ServiceState,
  type DeviceServiceDto,
  type ServiceRecoveryDto
} from './contracts.gen.js';

const STATE_LABELS: Partial<Record<ServiceState, string>> = {
  [ServiceState.Stopped]: 'Stopped',
  [ServiceState.StartPending]: 'Starting',
  [ServiceState.StopPending]: 'Stopping',
  [ServiceState.Running]: 'Running',
  [ServiceState.ContinuePending]: 'Resuming',
  [ServiceState.PausePending]: 'Pausing',
  [ServiceState.Paused]: 'Paused'
};

const STARTUP_LABELS: Partial<Record<ServiceStartupType, string>> = {
  [ServiceStartupType.Automatic]: 'Automatic',
  [ServiceStartupType.AutomaticDelayed]: 'Automatic (delayed start)',
  [ServiceStartupType.Manual]: 'Manual',
  [ServiceStartupType.Disabled]: 'Disabled',
  [ServiceStartupType.Boot]: 'Boot',
  [ServiceStartupType.System]: 'System'
};

export function serviceStateLabel(state: ServiceState): string {
  return STATE_LABELS[state] ?? 'Unknown';
}

export function startupTypeLabel(type: ServiceStartupType): string {
  return STARTUP_LABELS[type] ?? 'Unknown';
}

/** The startup types a request may ask for, in the order services.msc lists them. */
export const SETTABLE_STARTUP_TYPES: readonly ServiceStartupType[] = [
  ServiceStartupType.Automatic,
  ServiceStartupType.AutomaticDelayed,
  ServiceStartupType.Manual,
  ServiceStartupType.Disabled
];

export function isRunning(s: DeviceServiceDto): boolean {
  return s.state === ServiceState.Running || s.state === ServiceState.Paused || s.state === ServiceState.StartPending;
}

export function isAutomatic(s: DeviceServiceDto): boolean {
  return s.startupType === ServiceStartupType.Automatic || s.startupType === ServiceStartupType.AutomaticDelayed;
}

/**
 * Set to start on its own and not running - the one filter worth a name of its own, because it is
 * the first question asked of a machine where "something stopped working".
 */
export function isStoppedAutomatic(s: DeviceServiceDto): boolean {
  return isAutomatic(s) && s.state === ServiceState.Stopped;
}

export interface ServiceFilter {
  readonly label: string;
  readonly test: (s: DeviceServiceDto) => boolean;
}

export const SERVICE_FILTERS: readonly ServiceFilter[] = [
  { label: 'All services', test: () => true },
  // The agent's own judgement, the same rule the fleet signal and the history rows use: automatic
  // and stopped with an error, or never started. Narrower than "Automatic but stopped" on purpose.
  { label: 'Needs attention', test: s => !!s.attention },
  { label: 'Running', test: isRunning },
  { label: 'Stopped', test: s => s.state === ServiceState.Stopped },
  { label: 'Automatic but stopped', test: isStoppedAutomatic },
  { label: 'Disabled', test: s => s.startupType === ServiceStartupType.Disabled }
];

/** The two recovery actions the CRM may set. Reboot and run-a-program are reported, never offered. */
export const SETTABLE_FAILURE_ACTIONS: readonly ServiceFailureAction[] = [ServiceFailureAction.None, ServiceFailureAction.Restart];

export function failureActionLabel(action: ServiceFailureAction): string {
  switch (action) {
    case ServiceFailureAction.Restart:
      return 'Restart the service';
    case ServiceFailureAction.Reboot:
      return 'Restart the computer';
    case ServiceFailureAction.RunCommand:
      return 'Run a program';
    default:
      return 'Take no action';
  }
}

/** A port of `ServiceLimits.DescribeSeconds`: "15 minutes", "1 day", "never" for -1. */
export function describeSeconds(seconds: number): string {
  const plural = (n: number, unit: string): string => (n === 1 ? `1 ${unit}` : `${n} ${unit}s`);
  if (seconds < 0) return 'never';
  if (seconds === 0) return '0 seconds';
  if (seconds % 86400 === 0) return plural(seconds / 86400, 'day');
  if (seconds % 3600 === 0) return plural(seconds / 3600, 'hour');
  if (seconds % 60 === 0) return plural(seconds / 60, 'minute');
  return plural(seconds, 'second');
}

/** One line describing a recovery configuration, for a row's detail. */
export function describeRecovery(r: ServiceRecoveryDto): string {
  const short = (a: ServiceFailureAction): string =>
    a === ServiceFailureAction.Restart ? 'restart' : a === ServiceFailureAction.None ? 'nothing' : failureActionLabel(a).toLowerCase();
  if (r.firstFailure === ServiceFailureAction.None && r.secondFailure === ServiceFailureAction.None &&
      r.subsequentFailures === ServiceFailureAction.None) return 'On failure: take no action';
  return `On failure: ${short(r.firstFailure)}, then ${short(r.secondFailure)}, then ${short(r.subsequentFailures)}`;
}

/** Built-in log-on accounts, in the order services.msc offers them. */
export const BUILT_IN_ACCOUNTS: readonly { label: string; account: string }[] = [
  { label: 'Local System', account: 'LocalSystem' },
  { label: 'Local Service', account: 'NT AUTHORITY\\LocalService' },
  { label: 'Network Service', account: 'NT AUTHORITY\\NetworkService' }
];

export function isBuiltInAccount(account: string): boolean {
  const a = account.trim().toLowerCase();
  return BUILT_IN_ACCOUNTS.some(b => b.account.toLowerCase() === a) || a === '.\\localsystem';
}

/** Narrows by filter and by a case-insensitive match on display name, key name or description. */
export function filterServices(
  services: readonly DeviceServiceDto[],
  filter: ServiceFilter,
  search: string
): DeviceServiceDto[] {
  const needle = search.trim().toLowerCase();
  return services.filter(
    s =>
      filter.test(s) &&
      (!needle ||
        s.displayName.toLowerCase().includes(needle) ||
        s.name.toLowerCase().includes(needle) ||
        (s.description ?? '').toLowerCase().includes(needle))
  );
}
